import { Geometry } from "@babylonjs/core//Meshes/geometry";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { RouteInteractors } from '../tool/interactors';
import { CsgUtils } from "./CsgUtils";
import { VisibilityMode } from './../RoomManager';
import { ROUTE_ELEMENT_CLASSID } from './io/Types';
import { Util } from "../utils/Util";
import { CameraModeMask, CameraControl } from '@egr/wcf/modules/core/view/CameraControl';
import { serializeVector2, deserializeVector2 } from "@egr/wcf/modules/core/io";
import { ElementCategory, SceneElement, UndoOperation } from '@egr/wcf/modules/core/mdl';
import { ElementChangedFlags } from '@egr/wcf/modules/core/mdl';
import { LengthProperty, PropertyChangedResult, StringProperty } from "@egr/wcf/modules/core/prop";
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { eq, clamp } from "@egr/wcf/modules/utils/math";
import { signedAngleBetweenVectors2, VECTOR2_X, projectPointOnLine2ToRef, isPointOnSegment2, isParallel2, orientationToLine2, Orientation, VECTOR3_ZERO, intersectLineLine2 } from "@egr/wcf/modules/core/utils/MathUtils";
export var RouteEnding;
(function (RouteEnding) {
    RouteEnding[RouteEnding["Start"] = 0] = "Start";
    RouteEnding[RouteEnding["End"] = 1] = "End";
})(RouteEnding || (RouteEnding = {}));
export var RouteSide;
(function (RouteSide) {
    RouteSide[RouteSide["Left"] = 0] = "Left";
    RouteSide[RouteSide["Right"] = 1] = "Right";
})(RouteSide || (RouteSide = {}));
/*
*    2D route structure:
*
*    edge 3-----------------------edge 2
*      |                            |
*    start				           end
*      |                            |
*    edge 0-----------------------edge 1
*/
export class Route extends SceneElement {
    constructor(pRoomManager) {
        super(pRoomManager.app.model, new TransformNode("room:Route", pRoomManager.app.model.scene));
        this.mRoomManager = pRoomManager;
        this.mScene = pRoomManager.app.model.scene;
        this.mInteractors = new RouteInteractors(this.mRoomManager, this);
        this.mId = this.mRoomManager._getNextRouteId();
        this.mStart = new Vector2(0, 0);
        this.mEnd = new Vector2(1, 0);
        this.mHeight = 0.001;
        this.mThickness = 0.875;
        this.mWidth = "0.875";
        this.mEdges = new Array();
        this.mEdges.push(Vector2.Zero(), Vector2.Zero(), Vector2.Zero(), Vector2.Zero());
        this.mUnCutEdges = new Array();
        this.mUnCutEdges.push(Vector2.Zero(), Vector2.Zero(), Vector2.Zero(), Vector2.Zero());
        this.mLocalEdges = new Array();
        this.mLocalEdges.push(Vector2.Zero(), Vector2.Zero(), Vector2.Zero(), Vector2.Zero());
        this.mNode.freezeWorldMatrix();
        this.m2dMeshes = new Array();
        this.m3dMeshes = new Array();
        this.m3dNeedsUpdate = false;
        this.mStartCxn = {};
        this.mStartCxn.route = null;
        this.mStartCxn.routeEnding = null;
        this.mStartCxn.badCut = false;
        this.mEndCxn = {};
        this.mEndCxn.route = null;
        this.mEndCxn.routeEnding = null;
        this.mEndCxn.badCut = false;
        //this.mOpenings = new Array();
        this.mHandleTansformationChange = true;
        this.mPendingTransformMatrix = null;
    }
    get category() {
        //return (ElementCategory.None);
        return (ElementCategory.FloorPlan);
    }
    get id() {
        return (this.mId);
    }
    get startPoint() {
        return (this.mStart);
    }
    get startCxn() {
        return (this.mStartCxn);
    }
    get endPoint() {
        return (this.mEnd);
    }
    get endCxn() {
        return (this.mEndCxn);
    }
    get thickness() {
        return (this.mThickness);
    }
    get height() {
        return (this.mHeight);
    }
    canTranslate() {
        return (this.isSelected && this.mRoomManager.app.model.selection.length > 1);
    }
    canRotate() {
        return (this.isSelected && this.mRoomManager.app.model.selection.length > 1);
    }
    showInteractors(pShow) {
        this.mInteractors.showInteractors(pShow);
    }
    dispose() {
        super.dispose();
        this.mInteractors.dispose();
    }
    //TODO: clone ?
    clone(pNewParent) {
        let tClone = new Route(this.mRoomManager);
        tClone.setStartPoint(this.mStart.clone());
        tClone.setEndPoint(this.mEnd.clone());
        tClone.setThickness(this.mThickness);
        tClone.node.parent = pNewParent != null ? pNewParent : null;
        tClone.copyDataFrom(this);
        tClone.updateGeometry();
        return (tClone);
    }
    saveUndoData(pOperation) {
        return (this._saveUndoData());
    }
    loadUndoData(pOperation, pUndoData) {
        this._loadUndoData(pUndoData);
        if (pOperation === UndoOperation.Transform)
            this.mRoomManager._removeTransformedElement(this);
    }
    getSelectionHighlightBoundingBox() {
        if (this.mRoomManager.visibilityMode === VisibilityMode.Show2d) {
            let tBounds = this.boundingBox.clone();
            tBounds.max.y = 0.001;
            return (tBounds);
        }
        return (this.boundingBox);
    }
    getProperties() {
        let tPromise = super.getProperties().then((pProperties) => {
            let tProperties = pProperties;
            if (tProperties == null)
                tProperties = new Array();
            let tWidthChoices = new Array();
            for (let tWidth of this.mRoomManager._routeWidths) {
                tWidthChoices.push(this.mRoomManager._getRouteWidthsPropValue(tWidth));
            }
            //thickness
            let tThicknessProp = new LengthProperty(this, Route.PROP_KEY_THICKNESS, this.mRoomManager.resources.propertyRouteWidth);
            tThicknessProp.choiceList = true;
            tThicknessProp.allowCustomChoice = true;
            tThicknessProp.getChoices = () => Promise.resolve(tWidthChoices);
            tThicknessProp.getValue = () => this.mRoomManager._getRouteWidthsPropValue(this.mWidth);
            tThicknessProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null && this.setThickness(tValue))
                    return (Promise.resolve(PropertyChangedResult.Value));
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tProperties.push(tThicknessProp);
            return (tProperties);
        });
        return (tPromise);
    }
    serialize(pContext) {
        let tIORoute = {};
        tIORoute.classId = ROUTE_ELEMENT_CLASSID;
        tIORoute.id = this.mId;
        tIORoute.start = serializeVector2(this.mStart);
        tIORoute.end = serializeVector2(this.mEnd);
        tIORoute.height = this.mHeight;
        tIORoute.thickness = this.mThickness;
        if (this.mWidth !== this.mRoomManager._routeWidths[0])
            tIORoute.width = this.mWidth;
        if (this.mStartCxn.route != null) {
            tIORoute.startCxn = {};
            tIORoute.startCxn.routeId = this.mStartCxn.route.mId;
            if (this.mStartCxn.routeEnding != null) {
                tIORoute.startCxn.routeEnding = this.mStartCxn.routeEnding === RouteEnding.Start ? "s" : "e";
            }
        }
        if (this.mEndCxn.route != null) {
            tIORoute.endCxn = {};
            tIORoute.endCxn.routeId = this.mEndCxn.route.mId;
            if (this.mEndCxn.routeEnding != null) {
                tIORoute.endCxn.routeEnding = this.mEndCxn.routeEnding === RouteEnding.Start ? "s" : "e";
            }
        }
        return (tIORoute);
    }
    updateGeometry() {
        if (this.mRoomManager.isCollectingRouteUpdates) {
            this.mRoomManager._requestRouteUpdate(this);
            return;
        }
        if (!this.isInScene())
            return;
        this.updateTransformMatrix();
        this.update2dGeometry();
        this.update3dGeometry();
        this.notifyChange(ElementChangedFlags.BoundingBox);
    }
    setStartPoint(pPoint) {
        if (Util.vectorEquals2(pPoint, this.mStart) || Util.vectorEquals2(pPoint, this.mEnd))
            return;
        this.mRoomManager.beginCollectRouteUpdates();
        this.mStart.copyFrom(pPoint);
        this._setConnection(RouteEnding.Start, null, null);
        this.cutEdges();
        this.updateGeometry();
        this.updateConnectedRoutes(true);
        this.mRoomManager.endCollectRouteUpdates();
    }
    setEndPoint(pPoint) {
        if (Util.vectorEquals2(pPoint, this.mEnd) || Util.vectorEquals2(pPoint, this.mStart))
            return;
        this.mRoomManager.beginCollectRouteUpdates();
        this.mEnd.copyFrom(pPoint);
        this._setConnection(RouteEnding.End, null, null);
        this.cutEdges();
        this.updateGeometry();
        this.updateConnectedRoutes(true);
        this.mRoomManager.endCollectRouteUpdates();
    }
    moveRoute(pDirection) {
        this.mRoomManager.beginCollectRouteUpdates();
        let tDir = this.mEnd.subtract(this.mStart);
        this.mStart.addInPlace(pDirection);
        this.mEnd.addInPlace(pDirection);
        this.updateEndingPosition(true);
        //keep route length if route is connected on one side only
        if (this.mStartCxn.route != null && this.mEndCxn.route == null) {
            this.mEnd = this.mStart.add(tDir);
        }
        else if (this.mEndCxn.route != null && this.mStartCxn.route == null) {
            this.mStart = this.mEnd.subtract(tDir);
        }
        this.cutEdges();
        this.updateGeometry();
        this.updateConnectedRoutes(true);
        this.mRoomManager.endCollectRouteUpdates();
    }
    setThickness(pThickness) {
        if (eq(pThickness, this.mThickness))
            return (false);
        this.mRoomManager.beginCollectRouteUpdates();
        this.mThickness = clamp(pThickness, 0.01, 10);
        this.mWidth = StringProperty.ConvertValueToString(this.mThickness);
        this.cutEdges();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.updateConnectedRoutes(true);
        this.mRoomManager.endCollectRouteUpdates();
        return (true);
    }
    getRouteAngle() {
        let tVDir = this.mEnd.subtract(this.mStart).normalize();
        return (signedAngleBetweenVectors2(VECTOR2_X, tVDir));
    }
    getRouteLengthL() {
        return (Vector2.Distance(this.mEdges[3], this.mEdges[2]));
    }
    getRouteLengthR() {
        return (Vector2.Distance(this.mEdges[1], this.mEdges[0]));
    }
    getRouteLengthBase() {
        return (Vector2.Distance(this.mStart, this.mEnd));
    }
    distanceToPoint(pPoint, pAsLine = false) {
        projectPointOnLine2ToRef(pPoint, this.mStart, this.mEnd, !pAsLine, Temp.Vector2[0]);
        return (Vector2.Distance(Temp.Vector2[0], pPoint));
    }
    pointInside2DRoute(pV) {
        return Util.pointInQuadix(pV, this.mEdges[0], this.mEdges[1], this.mEdges[2], this.mEdges[3]) ||
            Util.pointOnQuadix(pV, this.mEdges[0], this.mEdges[1], this.mEdges[2], this.mEdges[3]);
    }
    pointOnRouteSide(pV, pSide) {
        if (pSide === RouteSide.Left &&
            (Util.vectorEquals2(pV, this.mEdges[2]) || Util.vectorEquals2(pV, this.mEdges[3]))) {
            return (true);
        }
        if (pSide === RouteSide.Right &&
            (Util.vectorEquals2(pV, this.mEdges[0]) || Util.vectorEquals2(pV, this.mEdges[1]))) {
            return (true);
        }
        if (pSide === RouteSide.Left) {
            return isPointOnSegment2(pV, this.mEdges[3], this.mEdges[2]);
        }
        return isPointOnSegment2(pV, this.mEdges[0], this.mEdges[1]);
    }
    isRouteParallel(pRoute) {
        if (pRoute == null)
            return (false);
        return (isParallel2(this.mStart, this.mEnd, pRoute.mStart, pRoute.mEnd));
    }
    isRouteOnLeftSide(pRoute) {
        if (pRoute == null)
            return (false);
        let tV = this.mStart;
        if (pRoute.mStartCxn.route === this) {
            tV = pRoute.mEnd;
        }
        else if (pRoute.mEndCxn.route === this) {
            tV = pRoute.mStart;
        }
        return (orientationToLine2(tV, this.mStart, this.mEnd)
            === Orientation.Clockwise);
    }
    isRouteOnRightSide(pRoute) {
        if (pRoute == null)
            return (false);
        let tV = this.mStart;
        if (pRoute.mStartCxn.route === this) {
            tV = pRoute.mEnd;
        }
        else if (pRoute.mEndCxn.route === this) {
            tV = pRoute.mStart;
        }
        return (orientationToLine2(tV, this.mStart, this.mEnd)
            === Orientation.CounterClockwise);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    get _edges() {
        return (this.mEdges);
    }
    /** @internal */
    get _localEdges() {
        return (this.mLocalEdges);
    }
    /** @internal */
    get _material() {
        return (this.mRoomManager._getRouteColorMaterial(this.mWidth));
    }
    /** @internal */
    _saveUndoData() {
        let tData = {};
        tData.start = this.mStart.clone();
        tData.startCxnRoute = this.mStartCxn.route;
        tData.startCxnRouteEnding = this.mStartCxn.routeEnding;
        tData.end = this.mEnd.clone();
        tData.endCxnRoute = this.mEndCxn.route;
        tData.endCxnRouteEnding = this.mEndCxn.routeEnding;
        tData.height = this.mHeight;
        tData.thickness = this.mThickness;
        tData.sideRoutes = new Map();
        for (let tRoute of this.mRoomManager.routes) {
            if (tRoute === this)
                continue;
            if (tRoute.mStartCxn.routeEnding == null && tRoute.mStartCxn.route === this) {
                tData.sideRoutes.set(tRoute, RouteEnding.Start);
            }
            else if (tRoute.mEndCxn.routeEnding == null && tRoute.mEndCxn.route === this) {
                tData.sideRoutes.set(tRoute, RouteEnding.End);
            }
        }
        return (tData);
    }
    /** @internal */
    _loadUndoData(pData) {
        if (pData == null)
            return;
        this.mRoomManager.beginCollectRouteUpdates();
        let tRouteDirections = this._getConnectedRouteDirections();
        this.mStart = pData.start.clone();
        this.mEnd = pData.end.clone();
        this.setThickness(pData.thickness);
        this.updateEdges();
        //restore end to end connections
        this._setConnection(RouteEnding.Start, pData.startCxnRoute, pData.startCxnRouteEnding);
        this._setConnection(RouteEnding.End, pData.endCxnRoute, pData.endCxnRouteEnding);
        //restore side connections
        pData.sideRoutes.forEach((pEnding, pRoute) => {
            pRoute._setConnection(pEnding, this, null);
        });
        this.updateEndingPosition(true);
        this.cutEdges();
        this.updateGeometry();
        this.updateConnectedRoutes(true);
        this._handleFlippedRoutes(tRouteDirections);
        this.mRoomManager.endCollectRouteUpdates();
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _setConnection(pEnding, pOtherRoute, pOtherEnding) {
        let tCxt = pEnding === RouteEnding.Start ? this.mStartCxn : this.mEndCxn;
        let tCxt2 = pEnding === RouteEnding.End ? this.mStartCxn : this.mEndCxn;
        if (pOtherRoute === tCxt.route || (pOtherRoute != null && pOtherRoute === tCxt2.route))
            return;
        //remove old connection
        if (tCxt.route != null) {
            if (tCxt.route.mStartCxn.route === this) {
                tCxt.route.mStartCxn.route = null;
                tCxt.route.mStartCxn.routeEnding = null;
            }
            if (tCxt.route.mEndCxn.route === this) {
                tCxt.route.mEndCxn.route = null;
                tCxt.route.mEndCxn.routeEnding = null;
            }
            let tOldRoute = tCxt.route;
            tCxt.route = null;
            tOldRoute.cutEdges();
            tOldRoute.updateGeometry();
        }
        tCxt.route = pOtherRoute;
        tCxt.routeEnding = pOtherEnding;
        //setup new end to end connection
        if (tCxt.route != null && pOtherEnding != null) {
            if (pOtherEnding === RouteEnding.Start) {
                tCxt.route.mStartCxn.route = this;
                tCxt.route.mStartCxn.routeEnding = pEnding;
            }
            else {
                tCxt.route.mEndCxn.route = this;
                tCxt.route.mEndCxn.routeEnding = pEnding;
            }
        }
        this.cutEdges();
        this.updateGeometry();
        if (tCxt.route != null) {
            tCxt.route.cutEdges();
            tCxt.route.updateGeometry();
        }
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _validateConnections(pMaxIterations, pCurrentIteration = 0) {
        let tOldS = this.mStartCxn.route;
        let tOldE = this.mEndCxn.route;
        if (this.mStartCxn.route != null) {
            let tW = this.mStartCxn.route;
            if (!(tW.isRouteOnLeftSide(this)) &&
                !(tW.isRouteOnRightSide(this)) &&
                this.mStartCxn.routeEnding == null) {
                this._setConnection(RouteEnding.Start, null, null);
            }
            else {
                let tV = Vector2.Center(this.mEdges[0], this.mEdges[3]);
                if (!tW.pointInside2DRoute(tV) && this.mStartCxn.routeEnding == null) {
                    this.mStart = tV;
                    this._setConnection(RouteEnding.Start, null, null);
                }
            }
        }
        if (this.mEndCxn.route != null) {
            let tW = this.mEndCxn.route;
            if (!(tW.isRouteOnLeftSide(this)) &&
                !(tW.isRouteOnRightSide(this)) &&
                this.mEndCxn.routeEnding == null) {
                this._setConnection(RouteEnding.End, null, null);
            }
            else {
                let tV = Vector2.Center(this.mEdges[1], this.mEdges[2]);
                if (!tW.pointInside2DRoute(tV) && this.mEndCxn.routeEnding == null) {
                    this.mEnd = tV;
                    this._setConnection(RouteEnding.End, null, null);
                }
            }
        }
        if (pCurrentIteration < pMaxIterations)
            this.validateConnectedRoutes(pMaxIterations, pCurrentIteration);
        return (tOldS != this.mStartCxn.route || tOldE != this.mEndCxn.route);
    }
    /** @internal */
    _findNewConnection(pEnding) {
        let tPoint1;
        let tPoint2;
        if (pEnding === RouteEnding.Start) {
            tPoint1 = this.mStart;
            tPoint2 = this.mEnd;
        }
        else {
            tPoint1 = this.mEnd;
            tPoint2 = this.mStart;
        }
        let tOtherRoute = this.mRoomManager.getNearestRoute(tPoint1, this);
        if (tOtherRoute != null && isPointOnSegment2(tPoint1, tOtherRoute.startPoint, tOtherRoute.endPoint)) {
            let tOtherEnding = null;
            if (Util.vectorEquals2(tPoint1, tOtherRoute.startPoint)) {
                if (tOtherRoute.startCxn.route == null)
                    tOtherEnding = RouteEnding.Start;
                else {
                    //check if current route is best possible
                    let tDist1 = tOtherRoute.distanceToPoint(tPoint2, true);
                    let tDist2 = tOtherRoute.startCxn.route.distanceToPoint(tPoint2, true);
                    if (tDist2 > tDist1)
                        tOtherRoute = tOtherRoute.startCxn.route;
                }
            }
            else if (Util.vectorEquals2(tPoint1, tOtherRoute.endPoint)) {
                if (tOtherRoute.endCxn.route == null)
                    tOtherEnding = RouteEnding.End;
                else {
                    //check if current route is best possible
                    let tDist1 = tOtherRoute.distanceToPoint(tPoint2, true);
                    let tDist2 = tOtherRoute.endCxn.route.distanceToPoint(tPoint2, true);
                    if (tDist2 > tDist1)
                        tOtherRoute = tOtherRoute.endCxn.route;
                }
            }
            this._setConnection(pEnding, tOtherRoute, tOtherEnding);
            return (true);
        }
        return (false);
    }
    /** @internal */
    _interactionFinished() {
        if (this.m3dNeedsUpdate) {
            this.update3dGeometry();
            this.update2dVisibility();
        }
    }
    /** @internal */
    _getConnectedRouteDirections() {
        let tMap = new Map();
        for (let tRoute of this.mRoomManager.routes) {
            if (tRoute === this || tRoute === tRoute.mStartCxn.route || tRoute === tRoute.mEndCxn.route)
                continue;
            if (tRoute.mStartCxn.route === this || tRoute.mEndCxn.route === this) {
                let tDir = tRoute.mEnd.subtract(tRoute.mStart).normalize();
                tMap.set(tRoute, tDir);
            }
        }
        return (tMap);
    }
    /** @internal */
    _handleFlippedRoutes(pDirectionMap) {
        if (pDirectionMap == null)
            return;
        pDirectionMap.forEach((pDir, pRoute) => {
            let tDir = pRoute.mEnd.subtract(pRoute.mStart).normalize();
            if (!Util.vectorEquals2(pDir, tDir)) {
                let tRoute = null;
                if (pRoute.mStartCxn.route !== this)
                    tRoute = pRoute.mStartCxn.route;
                else if (pRoute.mEndCxn.route !== this)
                    tRoute = pRoute.mEndCxn.route;
                if (tRoute != null) {
                    tRoute.cutEdges();
                    tRoute.updateGeometry();
                }
            }
        });
    }
    /** @internal */
    getAllSideRoutes(pRoutesLeft, pRoutesRight, pWithEndToEnd, pSort) {
        for (let tRoute of this.mRoomManager.routes) {
            if ((tRoute !== this &&
                ((tRoute !== this.mStartCxn.route && tRoute !== this.mEndCxn.route) || pWithEndToEnd)) &&
                (tRoute.mStartCxn.route === this || tRoute.mEndCxn.route === this)) {
                if (this.isRouteOnRightSide(tRoute)) {
                    pRoutesRight.push(tRoute);
                }
                else if (this.isRouteOnLeftSide(tRoute)) {
                    pRoutesLeft.push(tRoute);
                }
            }
        }
        Route.gCurrentRoute = this;
        pRoutesLeft.sort(Route.cmpRouteDistance);
        pRoutesRight.sort(Route.cmpRouteDistance);
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _getNeighbourDistance(pEnding, pSide) {
        let tResult = {
            distance: 0,
            refPos1: null,
            refPos2: null,
            negateDir: false
        };
        let tW = null;
        // ceck conditions
        if (pEnding === RouteEnding.Start) {
            if (this.mStartCxn.route == null)
                return (null);
            tW = this.mStartCxn.route;
            //check if it is a special end to end case
            if (this.mStartCxn.routeEnding != null && tW.mEndCxn.route == this &&
                (pSide === RouteSide.Left && tW.isRouteOnLeftSide(this) ||
                    pSide === RouteSide.Right && tW.isRouteOnRightSide(this))) {
                return this.getNeighbourDistanceEE(pEnding, pSide);
            }
            if (this.mStartCxn.routeEnding != null && tW.mStartCxn.route == this &&
                (pSide === RouteSide.Right && tW.isRouteOnLeftSide(this) ||
                    pSide === RouteSide.Left && tW.isRouteOnRightSide(this))) {
                return this.getNeighbourDistanceEE(pEnding, pSide);
            }
        }
        else {
            if (this.mEndCxn.route == null)
                return (null);
            tW = this.mEndCxn.route;
            //check if it is a special end to end case
            if (this.mEndCxn.routeEnding != null && tW.mEndCxn.route == this &&
                (pSide === RouteSide.Left && tW.isRouteOnLeftSide(this) ||
                    pSide === RouteSide.Right && tW.isRouteOnRightSide(this))) {
                return this.getNeighbourDistanceEE(pEnding, pSide);
            }
            if (this.mEndCxn.routeEnding != null && tW.mStartCxn.route == this &&
                (pSide === RouteSide.Right && tW.isRouteOnLeftSide(this) ||
                    pSide === RouteSide.Left && tW.isRouteOnRightSide(this))) {
                return this.getNeighbourDistanceEE(pEnding, pSide);
            }
        }
        if (this.isRouteParallel(tW)) {
            return (null);
        }
        //find neighbour routes
        let tWL = null;
        let tWR = null;
        let tV1 = Vector2.Zero();
        let tV2 = Vector2.Zero();
        let tNeg = 1;
        let tNeighbours = this.getNeighbours(pEnding);
        if (tNeighbours != null) {
            if (tW.isRouteOnLeftSide(this)) {
                tWL = tNeighbours.right;
                tWR = tNeighbours.left;
            }
            else {
                tWL = tNeighbours.left;
                tWR = tNeighbours.right;
            }
        }
        //get relevant points
        if (pSide === RouteSide.Left) {
            if (tWL != null) {
                if (tWL.mStartCxn.route === tW)
                    tV1 = tWL.mEdges[3];
                else
                    tV1 = tWL.mEdges[1];
            }
            else if (tW.isRouteOnLeftSide(this)) {
                tV1 = tW.mEdges[2];
            }
            else {
                tV1 = tW.mEdges[0];
            }
            if (this.mStartCxn.route === tW) {
                tV2 = this.mEdges[0];
            }
            else {
                tV2 = this.mEdges[2];
            }
            //to determine if it is a negative dist
            if (tW.isRouteOnLeftSide(this)) {
                tNeg = -1;
            }
        }
        else {
            if (tWR != null) {
                if (tWR.mStartCxn.route == tW) {
                    tV1 = tWR.mEdges[0];
                }
                else {
                    tV1 = tWR.mEdges[2];
                }
            }
            else if (tW.isRouteOnLeftSide(this)) {
                tV1 = tW.mEdges[3];
            }
            else {
                tV1 = tW.mEdges[1];
            }
            if (this.mStartCxn.route === tW) {
                tV2 = this.mEdges[3];
            }
            else {
                tV2 = this.mEdges[1];
            }
            //to determine if it is a negative dist
            if (tW.isRouteOnRightSide(this))
                tNeg = -1;
        }
        tResult.refPos1 = tV1;
        tResult.refPos2 = tV2;
        //negate distance if necessary
        if (!Util.vectorEquals2(tV1.subtract(tV2).normalize(), tW.mStart.subtract(tW.mEnd).normalize()))
            tResult.distance = (Vector2.Distance(tV1, tV2) * -tNeg);
        else
            tResult.distance = (Vector2.Distance(tV1, tV2) * tNeg);
        if (eq(tResult.distance, 0.0))
            tResult.distance = Math.abs(tResult.distance);
        return (tResult);
    }
    /** @internal */
    _finishLoading(pIORoute) {
        if (pIORoute == null)
            return;
        if (pIORoute.startCxn != null) {
            let tOtherEnding = null;
            if (pIORoute.startCxn.routeEnding != null)
                tOtherEnding = pIORoute.startCxn.routeEnding === "s" ? RouteEnding.Start : RouteEnding.End;
            let tOtherRoute = this.mRoomManager._getLoadedRouteById(pIORoute.startCxn.routeId);
            this._setConnection(RouteEnding.Start, tOtherRoute, tOtherEnding);
        }
        if (pIORoute.endCxn != null) {
            let tOtherEnding = null;
            if (pIORoute.endCxn.routeEnding != null)
                tOtherEnding = pIORoute.endCxn.routeEnding === "s" ? RouteEnding.Start : RouteEnding.End;
            let tOtherRoute = this.mRoomManager._getLoadedRouteById(pIORoute.endCxn.routeId);
            this._setConnection(RouteEnding.End, tOtherRoute, tOtherEnding);
        }
    }
    /** @internal */
    _updateVisibility() {
        this.update2dVisibility();
        this.update3dVisibility();
        //update selection bounds
        this.notifyChange(ElementChangedFlags.BoundingBox);
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _finishTransformation() {
        if (this.mPendingTransformMatrix == null)
            return;
        //disconnect routes which where not transformed
        if (this.mStartCxn.route != null && !this.mRoomManager._wasElementTransformed(this.mStartCxn.route))
            this._setConnection(RouteEnding.Start, null, null);
        if (this.mEndCxn.route != null && !this.mRoomManager._wasElementTransformed(this.mEndCxn.route))
            this._setConnection(RouteEnding.End, null, null);
        for (let tRoute of this.mRoomManager.routes) {
            if (tRoute === this)
                continue;
            if (!this.mRoomManager._wasElementTransformed(tRoute)) {
                if (tRoute.mStartCxn.route === this) {
                    tRoute._setConnection(RouteEnding.Start, null, null);
                }
                if (tRoute.mEndCxn.route === this) {
                    tRoute._setConnection(RouteEnding.End, null, null);
                }
            }
        }
        //transfom start and end
        this.mStart = Util.to2d(Vector3.TransformCoordinates(VECTOR3_ZERO, this.mPendingTransformMatrix));
        this.mEnd = Util.to2d(Vector3.TransformCoordinates(new Vector3(this.mTransformRouteLength, 0, 0), this.mPendingTransformMatrix));
        this.mPendingTransformMatrix = null;
        this.updateEdges();
        this.updateEndingPosition(true);
        this.cutEdges();
        this.updateGeometry();
        this.updateConnectedRoutes(true);
    }
    /** @internal */
    static _registerClass(pRoomManager) {
        pRoomManager.app.model.registerElementLoader(ROUTE_ELEMENT_CLASSID, (pApp, pContext, pIOElement) => {
            return (Route.deserialize(pRoomManager, pContext, pIOElement));
        });
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    handleImmediateChanges() {
        super.handleImmediateChanges();
        if (this.wasFlagChanged(ElementChangedFlags.Added))
            this.mRoomManager._addRoute(this);
        if ((this.wasFlagChanged(ElementChangedFlags.Removed)) &&
            (this.mRoomManager._removeRoute(this))) {
            this.mRoomManager.beginCollectRouteUpdates();
            this._setConnection(RouteEnding.Start, null, null);
            this._setConnection(RouteEnding.End, null, null);
            //disconnet sideroutes
            for (let tRoute of this.mRoomManager.routes) {
                if (tRoute === this)
                    continue;
                if (tRoute.mStartCxn.route === this)
                    tRoute._setConnection(RouteEnding.Start, null, null);
                else if (tRoute.mEndCxn.route === this)
                    tRoute._setConnection(RouteEnding.End, null, null);
            }
            this.mRoomManager.endCollectRouteUpdates();
        }
    }
    handleDelayedChanges() {
        super.handleDelayedChanges();
        if (this.wasFlagChanged(ElementChangedFlags.Transformation) && this.mHandleTansformationChange) {
            if (this.mRoomManager._transformationStarted()) {
                this.mPendingTransformMatrix = this.transform.clone();
                this.mTransformRouteLength = this.getRouteLengthBase();
                this.mRoomManager._addTransformedElement(this);
            }
            else
                this.updateTransformMatrix(); // reset transform
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    updateEndingPosition(pUpdateEndToEnd) {
        let tBadCut;
        let tV;
        let tFeedback = false;
        //start connection
        if (this.mStartCxn.route != null) {
            tBadCut = true;
            let tW = this.mStartCxn.route;
            if (!this.isRouteParallel(tW))
                tBadCut = false;
            if (Util.vectorEquals2(tW.mStart, tW.mEnd))
                tV = this.mStart;
            else
                tV = intersectLineLine2(this.mStart, this.mEnd, tW.mStart, tW.mEnd);
            if (tV == null || tBadCut) {
                if (this.mStartCxn.routeEnding != null && pUpdateEndToEnd && !tFeedback)
                    this._setConnection(RouteEnding.Start, null, null);
            }
            else if (!Util.vectorEquals2(this.mEnd, tV))
                this.mStart.copyFrom(tV);
            if (this.mStartCxn.routeEnding != null && pUpdateEndToEnd) {
                if (tW.mStartCxn.route === this && !Util.vectorEquals2(tW.mEnd, this.mStart)) {
                    tW.mStart.copyFrom(this.mStart);
                }
                else if (tW.mEndCxn.route === this && !Util.vectorEquals2(tW.mStart, this.mStart)) {
                    tW.mEnd.copyFrom(this.mStart);
                }
                this.updateEdges();
                tW.cutEdges();
                tW.updateGeometry();
            }
        }
        //end connection
        if (this.mEndCxn.route != null) {
            tBadCut = true;
            let tW = this.mEndCxn.route;
            if (!this.isRouteParallel(tW))
                tBadCut = false;
            if (Util.vectorEquals2(tW.mStart, tW.mEnd))
                tV = this.mEnd;
            else
                tV = intersectLineLine2(this.mStart, this.mEnd, tW.mStart, tW.mEnd);
            if (tV == null || tBadCut) {
                if (this.mEndCxn.routeEnding != null && pUpdateEndToEnd && !tFeedback)
                    this._setConnection(RouteEnding.End, null, null);
            }
            else if (!Util.vectorEquals2(this.mStart, tV))
                this.mEnd.copyFrom(tV);
            if (this.mEndCxn.routeEnding != null && pUpdateEndToEnd) {
                if (tW.mStartCxn.route === this && !Util.vectorEquals2(tW.mEnd, this.mEnd)) {
                    tW.mStart.copyFrom(this.mEnd);
                }
                else if (tW.mEndCxn.route === this && !Util.vectorEquals2(tW.mStart, this.mEnd)) {
                    tW.mEnd.copyFrom(this.mEnd);
                }
                this.updateEdges();
                tW.cutEdges();
                tW.updateGeometry();
            }
        }
    }
    updateTransformMatrix() {
        let tMRot = Matrix.RotationY(-this.getRouteAngle());
        let tMTrans = Matrix.Translation(this.mStart.x, 0.0, this.mStart.y);
        this.mHandleTansformationChange = false;
        this.setTransform(tMRot.multiply(tMTrans));
        this.mHandleTansformationChange = true;
        //update local edges
        let tTransInv = this.transformInverted;
        for (let i = 0; i < 4; ++i) {
            Vector3.TransformCoordinatesToRef(Util.to3d(this.mEdges[i]), tTransInv, Temp.Vector3[0]);
            this.mLocalEdges[i] = Util.to2d(Temp.Vector3[0]);
        }
    }
    updateEdges() {
        let tHT = this.mThickness / 2.0;
        let tAngle = this.getRouteAngle();
        let tCosA = Math.cos(tAngle);
        let tSinA = Math.sin(tAngle);
        this.mEdges[0].set(this.mStart.x + (tHT * tSinA), this.mStart.y - (tHT * tCosA));
        this.mEdges[3].set(this.mStart.x - (tHT * tSinA), this.mStart.y + (tHT * tCosA));
        this.mEdges[1].set(this.mEnd.x + (tHT * tSinA), this.mEnd.y - (tHT * tCosA));
        this.mEdges[2].set(this.mEnd.x - (tHT * tSinA), this.mEnd.y + (tHT * tCosA));
        for (let i = 0; i < 4; ++i)
            this.mUnCutEdges[i].copyFrom(this.mEdges[i]);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutEdges() {
        let tBadCut1;
        let tBadCut2;
        let tRes = 0;
        this.updateEdges();
        this.mStartCxn.badCut = false;
        this.mEndCxn.badCut = false;
        //start connection
        if (this.mStartCxn.route != null) {
            this.mStartCxn.badCut = true;
            tBadCut1 = true;
            tBadCut2 = true;
            let tW = this.mStartCxn.route;
            if (!this.isRouteParallel(tW)) {
                let tV1 = Vector2.Zero();
                let tV2 = Vector2.Zero();
                //ent to end
                if (this.mStartCxn.routeEnding != null) {
                    if (tW.mStartCxn.route === this) {
                        tBadCut1 = this.calcCutPointToRef(tW, 0, 1, 3, 2, tV1);
                        tBadCut2 = this.calcCutPointToRef(tW, 3, 2, 0, 1, tV2);
                    }
                    else if (tW.mEndCxn.route === this) {
                        tBadCut1 = this.calcCutPointToRef(tW, 0, 1, 1, 0, tV1);
                        tBadCut2 = this.calcCutPointToRef(tW, 3, 2, 2, 3, tV2);
                    }
                }
                else {
                    //side connection
                    if (tW.isRouteOnLeftSide(this)) {
                        tBadCut1 = this.calcSideCutPointToRef(tW, 0, 1, 2, 3, tV1);
                        tBadCut2 = this.calcSideCutPointToRef(tW, 3, 2, 2, 3, tV2);
                    }
                    else if (tW.isRouteOnRightSide(this)) {
                        tBadCut1 = this.calcSideCutPointToRef(tW, 0, 1, 1, 0, tV1);
                        tBadCut2 = this.calcSideCutPointToRef(tW, 3, 2, 1, 0, tV2);
                    }
                }
                if (!(tBadCut1 || tBadCut2)) {
                    this.mStartCxn.badCut = false;
                    this.mEdges[0] = tV1;
                    this.mEdges[3] = tV2;
                }
                else {
                    tRes += 1;
                }
            }
        }
        //end connection
        if (this.mEndCxn.route != null) {
            this.mEndCxn.badCut = true;
            tBadCut1 = true;
            tBadCut2 = true;
            let tW = this.mEndCxn.route;
            if (!this.isRouteParallel(tW)) {
                let tV1 = Vector2.Zero();
                let tV2 = Vector2.Zero();
                //end to end
                if (this.mEndCxn.routeEnding != null) {
                    if (tW.mStartCxn.route === this) {
                        tBadCut1 = this.calcCutPointToRef(tW, 1, 0, 0, 1, tV1);
                        tBadCut2 = this.calcCutPointToRef(tW, 2, 3, 3, 2, tV2);
                    }
                    else if (tW.mEndCxn.route === this) {
                        tBadCut1 = this.calcCutPointToRef(tW, 1, 0, 2, 3, tV1);
                        tBadCut2 = this.calcCutPointToRef(tW, 2, 3, 1, 0, tV2);
                    }
                }
                else {
                    if (tW.isRouteOnLeftSide(this)) {
                        tBadCut1 = this.calcSideCutPointToRef(tW, 1, 0, 3, 2, tV1);
                        tBadCut2 = this.calcSideCutPointToRef(tW, 2, 3, 3, 2, tV2);
                    }
                    else if (tW.isRouteOnRightSide(this)) {
                        tBadCut1 = this.calcSideCutPointToRef(tW, 1, 0, 0, 1, tV1);
                        tBadCut2 = this.calcSideCutPointToRef(tW, 2, 3, 0, 1, tV2);
                    }
                }
                if (!(tBadCut1 || tBadCut2)) {
                    this.mEndCxn.badCut = false;
                    this.mEdges[1] = tV1;
                    this.mEdges[2] = tV2;
                }
                else {
                    tRes += 2;
                }
            }
        }
        return (tRes);
    }
    calcCutPointToRef(pW2, pW1E1, pW1E2, pW2E1, pW2E2, pResult) {
        let tBadCut = false;
        //calculate cut Point
        let tVCut = intersectLineLine2(this.mUnCutEdges[pW1E1], this.mUnCutEdges[pW1E2], pW2.mUnCutEdges[pW2E1], pW2.mUnCutEdges[pW2E2]);
        if (tVCut == null)
            return (false);
        /////////
        //handle special cases (short routes, thin routes etc.)
        //checks if one side of a route is to short (would change its direction)
        let tV1 = tVCut.subtract(this.mUnCutEdges[pW1E2]).normalize();
        let tV2 = this.mUnCutEdges[pW1E1].subtract(this.mUnCutEdges[pW1E2]).normalize();
        let tV3 = tVCut.subtract(pW2.mUnCutEdges[pW2E2]).normalize();
        let tV4 = pW2.mUnCutEdges[pW2E1].subtract(pW2.mUnCutEdges[pW2E2]).normalize();
        let t12Equal = Util.vectorEquals2(tV1, tV2);
        let t34Equal = Util.vectorEquals2(tV3, tV4);
        if (!t12Equal && !t34Equal) {
            if (this.getRouteLengthBase() > pW2.getRouteLengthBase()) {
                tVCut = pW2.mUnCutEdges[pW2E2];
            }
            else {
                tVCut = this.mUnCutEdges[pW1E1];
            }
            tBadCut = true;
        }
        else {
            if (!t12Equal) {
                tVCut = this.mUnCutEdges[pW1E1];
                tBadCut = true;
            }
            if (!t34Equal) {
                tVCut = pW2.mUnCutEdges[pW2E2];
                tBadCut = true;
            }
        }
        //////////
        pResult.copyFrom(tVCut);
        return (tBadCut);
    }
    calcSideCutPointToRef(pW2, pW1E1, pW1E2, pW2E1, pW2E2, pResult) {
        let tBadCut = false;
        //calculate cut Point
        let tVCut = intersectLineLine2(this.mUnCutEdges[pW1E1], this.mUnCutEdges[pW1E2], pW2.mUnCutEdges[pW2E1], pW2.mUnCutEdges[pW2E2]);
        if (tVCut == null)
            return (false);
        /////////
        //handle special cases (short routes, thin routes etc.)
        //checks if one side of a route is to short (would change its direction)
        let tV1 = tVCut.subtract(this.mUnCutEdges[pW1E2]).normalize();
        let tV2 = this.mUnCutEdges[pW1E1].subtract(this.mUnCutEdges[pW1E2]).normalize();
        if (!Util.vectorEquals2(tV1, tV2)) {
            tVCut = this.mUnCutEdges[pW1E1];
            tBadCut = true;
        }
        //////////
        pResult.copyFrom(tVCut);
        return (tBadCut);
    }
    validateConnectedRoutes(pMaxIterations, pCurrentIteration) {
        if (this.mStartCxn.route != null)
            this.mStartCxn.route._validateConnections(pMaxIterations, pCurrentIteration + 1);
        if (this.mEndCxn.route != null)
            this.mEndCxn.route._validateConnections(pMaxIterations, pCurrentIteration + 1);
        for (let tRoute of this.mRoomManager.routes) {
            if (tRoute === this || tRoute === this.mStartCxn.route || tRoute === this.mEndCxn.route)
                continue;
            if (tRoute.mStartCxn.route === this || tRoute.mEndCxn.route === this) {
                tRoute._validateConnections(pMaxIterations, pCurrentIteration + 1);
            }
        }
    }
    updateConnectedRoutes(pIncludeEndConnections) {
        if (this.mStartCxn.route != null) {
            this.mStartCxn.route.updateEndingPosition(false);
            this.mStartCxn.route.cutEdges();
            this.mStartCxn.route.updateGeometry();
        }
        if (this.mEndCxn.route != null) {
            this.mEndCxn.route.updateEndingPosition(false);
            this.mEndCxn.route.cutEdges();
            this.mEndCxn.route.updateGeometry();
        }
        for (let tRoute of this.mRoomManager.routes) {
            if (tRoute === this || tRoute === this.mStartCxn.route || tRoute === this.mEndCxn.route)
                continue;
            if (tRoute.mStartCxn.route === this || tRoute.mEndCxn.route === this) {
                tRoute.updateEndingPosition(false);
                tRoute.cutEdges();
                tRoute.updateGeometry();
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getNeighbours(pEnding) {
        let tResult = {
            left: null,
            right: null
        };
        let tRoute = null;
        let tRoutes = new Array();
        let tTempRoutes = new Array();
        if (pEnding == RouteEnding.Start) {
            if (this.mStartCxn.route == null)
                return (null);
            tRoute = this.mStartCxn.route;
        }
        else {
            if (this.mEndCxn.route == null)
                return (null);
            tRoute = this.mEndCxn.route;
        }
        if (tRoute.isRouteOnLeftSide(this)) {
            tRoute.getAllSideRoutes(tRoutes, tTempRoutes, true, true);
        }
        else {
            tRoute.getAllSideRoutes(tTempRoutes, tRoutes, true, true);
        }
        if (tRoutes.length < 2)
            return (null);
        if (tRoutes[0] !== this) {
            for (let i = 0; i < tRoutes.length; ++i) {
                if (tRoutes[i] === this) {
                    tResult.left = tRoutes[i - 1];
                    break;
                }
            }
        }
        if (tRoutes[tRoutes.length - 1] !== this) {
            for (let i = 0; i < tRoutes.length; ++i) {
                if (tRoutes[i] === this) {
                    tResult.right = tRoutes[i + 1];
                    break;
                }
            }
        }
        return (tResult);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getNeighbourDistanceEE(pEnding, pSide) {
        let tResult = {
            distance: 0,
            refPos1: null,
            refPos2: null,
            negateDir: true
        };
        let tW = null;
        // ceck conditions
        if (pEnding === RouteEnding.Start) {
            if (this.mStartCxn.route == null)
                return (null);
            tW = this.mStartCxn.route;
        }
        else {
            if (this.mEndCxn.route == null)
                return (null);
            tW = this.mEndCxn.route;
        }
        if (this.isRouteParallel(tW)) {
            return (null);
        }
        //find neighbour route
        let tWN = null;
        let tV1 = Vector2.Zero();
        let tV2 = Vector2.Zero();
        let tNeg = 1;
        let tRoutes = new Array();
        let tTempRoutes = new Array();
        if (tW.isRouteOnRightSide(this)) {
            tW.getAllSideRoutes(tRoutes, tTempRoutes, true, true);
        }
        else {
            tW.getAllSideRoutes(tTempRoutes, tRoutes, true, true);
        }
        if (tRoutes.length > 0) {
            if (tW.mStartCxn.route === this) {
                tWN = tRoutes[0];
            }
            else {
                tWN = tRoutes[tRoutes.length - 1];
            }
        }
        if (tW.isRouteOnLeftSide(this)) {
            if (tW.mStartCxn.route === this) {
                if (tWN != null) {
                    if (tWN.mStartCxn.route === tW)
                        tV1 = tWN.mEdges[0];
                    else
                        tV1 = tWN.mEdges[2];
                }
                else
                    tV1 = tW.mEdges[1];
                if (this.mStartCxn.route === tW)
                    tV2 = this.mEdges[3];
                else
                    tV2 = this.mEdges[1];
                tNeg = -1;
            }
            else {
                if (tWN != null) {
                    if (tWN.mStartCxn.route === tW)
                        tV1 = tWN.mEdges[3];
                    else
                        tV1 = tWN.mEdges[1];
                }
                else
                    tV1 = tW.mEdges[0];
                if (this.mStartCxn.route === tW)
                    tV2 = this.mEdges[0];
                else
                    tV2 = this.mEdges[2];
            }
        }
        else if (tW.mStartCxn.route === this) {
            if (tWN != null) {
                if (tWN.mStartCxn.route === tW)
                    tV1 = tWN.mEdges[3];
                else
                    tV1 = tWN.mEdges[1];
            }
            else
                tV1 = tW.mEdges[2];
            if (this.mStartCxn.route === tW)
                tV2 = this.mEdges[0];
            else
                tV2 = this.mEdges[2];
            tNeg = -1;
        }
        else {
            if (tWN != null) {
                if (tWN.mStartCxn.route === tW)
                    tV1 = tWN.mEdges[0];
                else
                    tV1 = tWN.mEdges[2];
            }
            else
                tV1 = tW.mEdges[3];
            if (this.mStartCxn.route === tW)
                tV2 = this.mEdges[3];
            else
                tV2 = this.mEdges[1];
        }
        tResult.refPos1 = tV1;
        tResult.refPos2 = tV2;
        //negate distance if necessary
        if (!Util.vectorEquals2(tV1.subtract(tV2).normalize(), tW.mStart.subtract(tW.mEnd).normalize()))
            tResult.distance = (Vector2.Distance(tV1, tV2) * -tNeg);
        else
            tResult.distance = (Vector2.Distance(tV1, tV2) * tNeg);
        if (eq(tResult.distance, 0.0))
            tResult.distance = Math.abs(tResult.distance);
        return (tResult);
    }
    update2dGeometry() {
        this.dispose2dMeshes();
        //hatch
        let tVertexData = new VertexData();
        tVertexData.positions = new Float32Array(12);
        tVertexData.normals = new Float32Array(12);
        tVertexData.uvs = new Float32Array(8);
        tVertexData.indices = new Uint16Array(6);
        for (let i = 0; i < 4; ++i) {
            let tI2 = i * 2;
            let tI3 = i * 3;
            tVertexData.positions[tI3 + 0] = this.mLocalEdges[i].x;
            tVertexData.positions[tI3 + 1] = 0;
            tVertexData.positions[tI3 + 2] = this.mLocalEdges[i].y;
            tVertexData.normals[tI3 + 0] = 0;
            tVertexData.normals[tI3 + 1] = 1;
            tVertexData.normals[tI3 + 2] = 0;
            tVertexData.uvs[tI2 + 0] = this.mEdges[i].x;
            tVertexData.uvs[tI2 + 1] = this.mEdges[i].y;
        }
        tVertexData.indices[0] = 0;
        tVertexData.indices[1] = 1;
        tVertexData.indices[2] = 2;
        tVertexData.indices[3] = 0;
        tVertexData.indices[4] = 2;
        tVertexData.indices[5] = 3;
        let tMesh = new Mesh("", this.mScene);
        let tGeom = new Geometry(Geometry.RandomId(), this.mScene, tVertexData);
        tGeom.applyToMesh(tMesh);
        tMesh.material = this.mRoomManager.route2dMaterial;
        tMesh.parent = this.mNode;
        tMesh.position.y = 0.008;
        tMesh.visibility = 0.8;
        tMesh.freezeWorldMatrix();
        ShadowGenerator.disableMeshShadow(tMesh);
        this.m2dMeshes.push(tMesh);
        this.update2dVisibility();
    }
    update2dVisibility() {
        for (let tMesh of this.m2dMeshes)
            //visible in all views
            CameraControl.SetMeshCameraModeMask(tMesh, null);
    }
    update3dGeometry() {
        this.dispose3dMeshes();
        //no 3d update during interaction
        if (this.mRoomManager.isInteracting) {
            this.m3dNeedsUpdate = true;
            return;
        }
        this.m3dNeedsUpdate = false;
        let tCSG = CsgUtils.extrudeRoute(this);
        if (tCSG == null)
            return;
        //create meshes
        let tMeshes = CsgUtils.buildMeshes(tCSG, this.mScene);
        if (tMeshes != null) {
            for (let tMesh of tMeshes) {
                if (tMesh.material == null)
                    tMesh.material = this.mRoomManager.routeDefaultMaterial;
                tMesh.parent = this.mNode;
                tMesh.freezeWorldMatrix();
                //not visible in top and bottom view
                CameraControl.SetMeshCameraModeMask(tMesh, CameraModeMask.All &
                    ~CameraModeMask.Top & ~CameraModeMask.Bottom);
                this.m3dMeshes.push(tMesh);
            }
        }
        this.update3dVisibility();
    }
    update3dVisibility() {
        for (let tMesh of this.m3dMeshes) {
            if (this.mRoomManager.visibilityMode === 0) {
                tMesh.setEnabled(false);
            }
            else {
                tMesh.setEnabled(false);
            }
        }
    }
    dispose2dMeshes() {
        if (this.m2dMeshes.length === 0)
            return;
        for (let tMesh of this.m2dMeshes)
            tMesh.dispose();
        this.m2dMeshes = new Array();
    }
    dispose3dMeshes() {
        if (this.m3dMeshes.length === 0)
            return;
        for (let tMesh of this.m3dMeshes)
            tMesh.dispose();
        this.m3dMeshes = new Array();
    }
    static deserialize(pRoomManager, pContext, pIOElement) {
        if (pIOElement == null)
            return (null);
        let tIORoute = pIOElement;
        let tRoute = new Route(pRoomManager);
        tRoute.setStartPoint(deserializeVector2(tIORoute.start));
        tRoute.setEndPoint(deserializeVector2(tIORoute.end));
        tRoute.mHeight = tIORoute.height;
        tRoute.mThickness = tIORoute.thickness;
        if (tIORoute.width != null)
            tRoute.mWidth = tIORoute.width;
        tRoute.updateEdges();
        tRoute.updateGeometry();
        tRoute.node.setEnabled(pRoomManager.RoutesVisible);
        pRoomManager._notifyRouteLoaded(tRoute, tIORoute);
        return (tRoute);
    }
    static cmpRouteDistance(pW1, pW2) {
        let tDist1 = 0;
        let tDist2 = 0;
        let tV;
        if (pW1 !== pW2 && Route.gCurrentRoute != null && pW1 != null && pW2 != null) {
            if (pW1.mStartCxn.route === Route.gCurrentRoute)
                tV = pW1.mStart;
            else
                tV = pW1.mEnd;
            tDist1 = Vector2.Distance(tV, Route.gCurrentRoute.mStart);
            if (pW2.mStartCxn.route === Route.gCurrentRoute)
                tV = pW2.mStart;
            else
                tV = pW2.mEnd;
            tDist2 = Vector2.Distance(tV, Route.gCurrentRoute.mStart);
            return (tDist1 - tDist2);
        }
        return (0);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Route.PROP_KEY_THICKNESS = "room:Route:width";
Route.gCurrentRoute = null;
/**
 * Temporary pre-allocated objects for internal use.
 */
class Temp {
}
Temp.Vector2 = [Vector2.Zero(), Vector2.Zero()]; // 2 * Vector2
Temp.Vector3 = [Vector3.Zero(), Vector3.Zero()]; // 2 * Vector3
var Line2dMode;
(function (Line2dMode) {
    Line2dMode[Line2dMode["Route"] = 0] = "Route";
    Line2dMode[Line2dMode["Floor"] = 1] = "Floor";
    Line2dMode[Line2dMode["Area"] = 2] = "Area";
    Line2dMode[Line2dMode["Hatch"] = 3] = "Hatch";
})(Line2dMode || (Line2dMode = {}));
